import {
    Alert,
    Autocomplete,
    Badge,
    Button,
    Heading,
    Pane,
    SearchInput,
    SelectField,
    Spinner,
    Strong,
    Switch,
    Text,
    TextInput,
    toaster,
} from 'evergreen-ui';
import React from 'react';
import { Link } from 'react-router-dom';
import Request from '../../../util/Api';

const CARD_IMAGE_BUCKET = process.env.REACT_APP_CARD_IMAGE_BUCKET;

const csvEscape = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
};

const buildCsv = (rows) => {
    const headers = ['id', 'code', 'rarity', 'quantity', 'version'];
    const lines = [headers.join(',')];
    for (const row of rows) {
        lines.push([row.id, row.code, row.rarity, row.quantity, row.version].map(csvEscape).join(','));
    }
    return lines.join('\r\n');
};

const cardImageUrl = (path) => {
    if (!path || !CARD_IMAGE_BUCKET) return null;
    return CARD_IMAGE_BUCKET + '/' + path;
};

class SingleWizard extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            // reference data
            stockAccounts: [],
            cardGames: [],
            expansions: [],
            loadingExpansions: false,

            // filters
            stockAccountId: '',
            cardGameId: '',
            cardGameMarketId: '',
            expansionId: '',
            searchInput: '',
            searchQuery: '',

            // results
            cards: [],
            loadingCards: false,
            truncated: false,

            // edits: { [cardId]: number }
            edits: {},
            // snapshot of card data for every edited card so they can be
            // re-rendered at the top even after the user changes filters
            // and the current results no longer include them.
            // { [cardId]: cardObject }
            editedCards: {},

            // selection / preview
            selectedId: null,

            // ui toggles
            onlyChangesInCsv: true,
            sortChangedFirst: true,
        };

        this.inputRefs = new Map();
        this.searchDebounce = null;
    }

    async componentDidMount() {
        try {
            const [stockAccountsRes, cardGamesRes] = await Promise.all([
                Request.get('/stock-accounts', {}, { _start: 0, _end: 1000 }),
                Request.get('/card-games', {}, {}),
            ]);
            this.setState({
                stockAccounts: stockAccountsRes.data || stockAccountsRes || [],
                cardGames: cardGamesRes.data || cardGamesRes || [],
            });
        } catch (err) {
            toaster.danger('Nu s-au putut încărca depozitele sau jocurile');
        }
    }

    componentWillUnmount() {
        if (this.searchDebounce) clearTimeout(this.searchDebounce);
    }

    // ---------- data loading ----------

    loadExpansions = async (cardMarketId) => {
        if (!cardMarketId) {
            this.setState({ expansions: [], expansionId: '' });
            return;
        }
        this.setState({ loadingExpansions: true });
        try {
            const res = await Request.get('/card-games/card-expansions', {}, {
                _start: 0,
                _end: 2000,
                _sort: 'releaseDate',
                _order: 'DESC',
                extraFilters: JSON.stringify({ cardGameId: parseInt(cardMarketId) }),
            });
            this.setState({
                expansions: res.data || res || [],
                expansionId: '',
            });
        } catch (err) {
            toaster.danger('Nu s-au putut încărca extensiile');
        } finally {
            this.setState({ loadingExpansions: false });
        }
    };

    fetchCards = async () => {
        const { stockAccountId, cardGameMarketId, expansionId, searchQuery } = this.state;

        if (!stockAccountId) {
            this.setState({ cards: [], truncated: false });
            return;
        }

        const hasAnyScope =
            (searchQuery && searchQuery.trim().length >= 2) || expansionId || cardGameMarketId;

        if (!hasAnyScope) {
            this.setState({ cards: [], truncated: false });
            return;
        }

        this.setState({ loadingCards: true });
        try {
            const params = { stockAccountId };
            if (searchQuery && searchQuery.trim().length >= 2) {
                params.q = searchQuery.trim();
            }
            if (cardGameMarketId) params.gameId = cardGameMarketId;
            if (expansionId) params.expansionId = expansionId;
            params.limit = expansionId ? 500 : 200;

            const res = await Request.get('/card-games/inventory/search', {}, params);
            const cards = res.data || [];

            this.setState((prev) => {
                let selectedId = prev.selectedId;
                if (selectedId && !cards.find((c) => c.id === selectedId)) {
                    selectedId = cards.length > 0 ? cards[0].id : null;
                } else if (!selectedId && cards.length > 0) {
                    selectedId = cards[0].id;
                }
                return {
                    cards,
                    truncated: !!res.truncated,
                    selectedId,
                };
            });
        } catch (err) {
            toaster.danger('Căutarea a eșuat: ' + (err.message || 'eroare necunoscută'));
            this.setState({ cards: [], truncated: false });
        } finally {
            this.setState({ loadingCards: false });
        }
    };

    // ---------- filter handlers ----------

    handleStockAccountChange = (e) => {
        this.setState({ stockAccountId: e.target.value }, this.fetchCards);
    };

    handleCardGameSelect = (game) => {
        const id = game ? String(game.id) : '';
        const cardMarketId = game ? String(game.cardMarketId || '') : '';
        this.setState({ cardGameId: id, cardGameMarketId: cardMarketId }, () => {
            this.loadExpansions(cardMarketId).then(this.fetchCards);
        });
    };

    handleExpansionSelect = (expansion) => {
        this.setState(
            { expansionId: expansion ? String(expansion.id) : '' },
            this.fetchCards
        );
    };

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ searchInput: value });
        if (this.searchDebounce) clearTimeout(this.searchDebounce);
        this.searchDebounce = setTimeout(() => {
            this.setState({ searchQuery: value }, this.fetchCards);
        }, 300);
    };

    handleResetFilters = () => {
        this.setState(
            {
                cardGameId: '',
                cardGameMarketId: '',
                expansionId: '',
                expansions: [],
                searchInput: '',
                searchQuery: '',
                cards: [],
                truncated: false,
            },
            this.fetchCards
        );
    };

    // ---------- editing ----------

    handleQuantityChange = (cardId, value) => {
        // Normalize: strip non-digits, allow empty.
        const cleaned = String(value).replace(/[^\d]/g, '');
        this.setState((prev) => {
            const card =
                prev.cards.find((c) => c.id === cardId) ||
                prev.editedCards[cardId];
            if (!card) return null;

            const edits = { ...prev.edits };
            const editedCards = { ...prev.editedCards };
            if (cleaned === '') {
                // Empty means "no edit" (revert to current)
                delete edits[cardId];
                delete editedCards[cardId];
            } else {
                const num = parseInt(cleaned, 10);
                if (Number.isFinite(num) && num >= 0) {
                    if (num === card.currentQuantity) {
                        delete edits[cardId];
                        delete editedCards[cardId];
                    } else {
                        edits[cardId] = num;
                        editedCards[cardId] = card;
                    }
                }
            }
            return { edits, editedCards, selectedId: cardId };
        });
    };

    handleQuantityKeyDown = (e, cardId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.focusNextInput(cardId);
        }
    };

    registerInputRef = (cardId, ref) => {
        if (ref) {
            this.inputRefs.set(cardId, ref);
        } else {
            this.inputRefs.delete(cardId);
        }
    };

    focusNextInput = (currentCardId) => {
        const ordered = this.getOrderedCards();
        const idx = ordered.findIndex((c) => c.id === currentCardId);
        if (idx === -1) return;
        for (let i = idx + 1; i < ordered.length; i++) {
            const ref = this.inputRefs.get(ordered[i].id);
            if (ref) {
                ref.focus();
                if (typeof ref.select === 'function') ref.select();
                this.setState({ selectedId: ordered[i].id });
                return;
            }
        }
    };

    handleSelectRow = (cardId) => {
        this.setState({ selectedId: cardId });
        const ref = this.inputRefs.get(cardId);
        if (ref) ref.focus();
    };

    handleResetEdits = () => {
        if (Object.keys(this.state.edits).length === 0) return;
        if (!window.confirm('Sigur vrei să anulezi toate modificările?')) return;
        this.setState({ edits: {}, editedCards: {} });
    };

    // ---------- sorting ----------

    // Build the rendered list. Edited cards are always shown — even when
    // they no longer match the current filters — and (when sortChangedFirst
    // is on) they are pinned to the top.
    getOrderedCards = () => {
        const { cards, edits, editedCards, sortChangedFirst } = this.state;

        const editedIds = Object.keys(edits).map((k) => parseInt(k, 10));
        const seen = new Set();

        // Edited rows first, in a stable order (by id) so their position
        // doesn't jump while the user is typing in another row.
        const changed = [];
        editedIds
            .sort((a, b) => a - b)
            .forEach((id) => {
                const card = cards.find((c) => c.id === id) || editedCards[id];
                if (card && !seen.has(id)) {
                    changed.push(card);
                    seen.add(id);
                }
            });

        const rest = [];
        for (const c of cards) {
            if (seen.has(c.id)) continue;
            rest.push(c);
            seen.add(c.id);
        }

        if (!sortChangedFirst) {
            // Even without pinning we still want edited-but-out-of-scope
            // cards to be visible; append them at the end in that case.
            const inScopeIds = new Set(cards.map((c) => c.id));
            const orphans = changed.filter((c) => !inScopeIds.has(c.id));
            return [...cards, ...orphans];
        }

        return [...changed, ...rest];
    };

    // ---------- save ----------

    handleSaveCsv = () => {
        const { cards, edits, editedCards, onlyChangesInCsv, stockAccountId } = this.state;

        const editedIds = new Set(Object.keys(edits).map((k) => parseInt(k, 10)));

        if (onlyChangesInCsv && editedIds.size === 0) {
            toaster.warning('Nu ai făcut nicio modificare. Introdu cantitățile noi mai întâi.');
            return;
        }

        if (!onlyChangesInCsv && cards.length === 0) {
            toaster.warning('Nu sunt cărți în listă');
            return;
        }

        let rowsToExport;
        if (onlyChangesInCsv) {
            // Always export every edited card, even ones currently filtered out.
            rowsToExport = Array.from(editedIds)
                .map((id) => cards.find((c) => c.id === id) || editedCards[id])
                .filter(Boolean);
        } else {
            rowsToExport = cards.slice();
        }

        const csvRows = rowsToExport.map((c) => ({
            id: c.id,
            code: c.code || '',
            rarity: c.rarity || '',
            quantity: edits[c.id] !== undefined ? edits[c.id] : c.currentQuantity,
            version: c.version || 'normal',
        }));

        const csv = buildCsv(csvRows);
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const stamp = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `single-wizard_sa${stockAccountId || 'x'}_${stamp}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toaster.success(`CSV descărcat (${csvRows.length} rând${csvRows.length === 1 ? '' : 'uri'})`);
    };

    // ---------- rendering ----------

    renderPreview() {
        const { selectedId, cards, edits, editedCards } = this.state;
        const card =
            cards.find((c) => c.id === selectedId) ||
            (selectedId != null ? editedCards[selectedId] : null);

        if (!card) {
            return (
                <Pane
                    padding={16}
                    background="tint1"
                    borderRadius={8}
                    minHeight={520}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Text color="muted" textAlign="center">
                        Selectează o carte din lista din dreapta pentru a vedea detalii și o
                        previzualizare mare.
                    </Text>
                </Pane>
            );
        }

        const fullImage = cardImageUrl(card.image || card.thumbnail);
        const editedQty = edits[card.id];
        const isChanged = editedQty !== undefined;

        return (
            <Pane padding={16} background="tint1" borderRadius={8}>
                <Pane
                    width="100%"
                    height={360}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    background="white"
                    borderRadius={6}
                    marginBottom={12}
                    overflow="hidden"
                >
                    {fullImage ? (
                        <img
                            src={fullImage}
                            alt={card.name}
                            style={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    ) : (
                        <Text color="muted">Fără imagine</Text>
                    )}
                </Pane>

                <Heading size={500} marginBottom={4}>{card.name}</Heading>
                {card.expansion && (
                    <Text size={300} color="muted" display="block" marginBottom={8}>
                        {card.expansion.code ? `[${card.expansion.code}] ` : ''}
                        {card.expansion.name}
                        {card.cardGame ? ` · ${card.cardGame.name}` : ''}
                    </Text>
                )}

                <Pane display="grid" gridTemplateColumns="auto 1fr" columnGap={12} rowGap={4} fontSize={13}>
                    <Strong>Cod:</Strong>
                    <Text>{card.code || '—'}</Text>

                    <Strong>Număr:</Strong>
                    <Text>{card.number || '—'}</Text>

                    <Strong>Raritate:</Strong>
                    <Text>{card.rarity || '—'}</Text>

                    <Strong>Variantă:</Strong>
                    <Text>{card.version || 'normal'}</Text>

                    <Strong>ID intern:</Strong>
                    <Text>{card.id}</Text>

                    <Strong>Stoc curent:</Strong>
                    <Text>{card.currentQuantity}</Text>

                    {isChanged && (
                        <>
                            <Strong color="success">Stoc nou:</Strong>
                            <Text color="success">
                                {editedQty}{' '}
                                <Strong color={editedQty - card.currentQuantity > 0 ? 'success' : 'danger'}>
                                    ({editedQty - card.currentQuantity > 0 ? '+' : ''}
                                    {editedQty - card.currentQuantity})
                                </Strong>
                            </Text>
                        </>
                    )}
                </Pane>
            </Pane>
        );
    }

    renderRow = (card) => {
        const { selectedId, edits } = this.state;
        const isSelected = card.id === selectedId;
        const editedQty = edits[card.id];
        const isChanged = editedQty !== undefined;
        const thumbUrl = cardImageUrl(card.thumbnail);

        return (
            <Pane
                key={card.id}
                display="grid"
                gridTemplateColumns="48px 1fr 110px 110px 70px 70px 110px"
                columnGap={8}
                alignItems="center"
                padding={8}
                background={isSelected ? 'blueTint' : isChanged ? '#FFFBE6' : 'white'}
                borderBottom="muted"
                cursor="pointer"
                onClick={() => this.handleSelectRow(card.id)}
            >
                <Pane
                    width={48}
                    height={64}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    background="tint2"
                    borderRadius={4}
                    overflow="hidden"
                >
                    {thumbUrl ? (
                        <img
                            src={thumbUrl}
                            alt=""
                            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                        />
                    ) : null}
                </Pane>

                <Pane minWidth={0}>
                    <Text fontWeight={500} display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                        {card.name}
                    </Text>
                    {card.expansion && (
                        <Text size={300} color="muted" display="block" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                            {card.expansion.code ? `[${card.expansion.code}] ` : ''}{card.expansion.name}
                        </Text>
                    )}
                </Pane>

                <Text size={300} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {card.code || '—'}
                </Text>

                <Text size={300} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {card.version}{card.rarity ? ` · ${card.rarity}` : ''}
                </Text>

                <Text size={300} textAlign="right">
                    {card.currentQuantity}
                </Text>

                <Pane textAlign="center">
                    {isChanged && (
                        <Badge color={editedQty - card.currentQuantity > 0 ? 'green' : 'red'}>
                            {editedQty - card.currentQuantity > 0 ? '+' : ''}
                            {editedQty - card.currentQuantity}
                        </Badge>
                    )}
                </Pane>

                <TextInput
                    type="text"
                    inputMode="numeric"
                    width={100}
                    placeholder={String(card.currentQuantity)}
                    value={editedQty !== undefined ? String(editedQty) : ''}
                    onChange={(e) => this.handleQuantityChange(card.id, e.target.value)}
                    onFocus={() => this.setState({ selectedId: card.id })}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => this.handleQuantityKeyDown(e, card.id)}
                    ref={(ref) => this.registerInputRef(card.id, ref)}
                />
            </Pane>
        );
    };

    render() {
        const {
            stockAccounts,
            cardGames,
            expansions,
            stockAccountId,
            cardGameId,
            expansionId,
            searchInput,
            cards,
            loadingCards,
            loadingExpansions,
            truncated,
            edits,
            onlyChangesInCsv,
            sortChangedFirst,
        } = this.state;

        const selectedGame = cardGames.find((g) => String(g.id) === cardGameId) || null;
        const selectedExpansion = expansions.find((e) => String(e.id) === expansionId) || null;
        const orderedCards = this.getOrderedCards();
        const changesCount = Object.keys(edits).length;

        return (
            <Pane padding={20}>
                <Pane display="flex" alignItems="center" marginBottom={4}>
                    <Heading size={700}>Asistent introducere singles</Heading>
                    <Pane flex={1} />
                    <Link to="/cards">
                        <Button>Înapoi la singles</Button>
                    </Link>
                </Pane>
                <Text color="muted" display="block" marginBottom={16}>
                    Caută cărțile, scrie noua cantitate disponibilă în depozit, apoi descarcă
                    fișierul CSV. Încarcă-l în <Strong>Inventory Resync</Strong> pentru a aplica
                    modificările.
                </Text>

                {/* Filters */}
                <Pane background="tint1" padding={16} borderRadius={8} marginBottom={16}>
                    <Heading size={400} marginBottom={12}>1. Alege depozitul și ce vrei să cauți</Heading>
                    <Pane display="flex" flexWrap="wrap" gap={12} alignItems="flex-end">
                        <SelectField
                            label="Depozit"
                            value={stockAccountId}
                            onChange={this.handleStockAccountChange}
                            width={220}
                            marginBottom={0}
                        >
                            <option value="">— alege —</option>
                            {stockAccounts.map((sa) => (
                                <option key={sa.id} value={sa.id}>{sa.name} (#{sa.id})</option>
                            ))}
                        </SelectField>

                        <Pane>
                            <Strong size={300} display="block" marginBottom={4}>Joc</Strong>
                            <Autocomplete
                                items={cardGames}
                                itemToString={(g) => (g ? g.name : '')}
                                selectedItem={selectedGame}
                                onChange={this.handleCardGameSelect}
                            >
                                {({ getInputProps, getRef, inputValue, openMenu }) => (
                                    <TextInput
                                        ref={getRef}
                                        width={200}
                                        placeholder="Caută joc..."
                                        value={inputValue}
                                        onFocus={openMenu}
                                        {...getInputProps()}
                                    />
                                )}
                            </Autocomplete>
                        </Pane>

                        <Pane>
                            <Strong size={300} display="block" marginBottom={4}>
                                Extensie {loadingExpansions ? '(se încarcă...)' : ''}
                            </Strong>
                            <Autocomplete
                                items={expansions}
                                itemToString={(e) => {
                                    if (!e) return '';
                                    const code = e.code ? `[${e.code}] ` : '';
                                    return `${code}${e.name}`;
                                }}
                                selectedItem={selectedExpansion}
                                onChange={this.handleExpansionSelect}
                            >
                                {({ getInputProps, getRef, inputValue, openMenu }) => (
                                    <TextInput
                                        ref={getRef}
                                        width={260}
                                        placeholder="Caută extensie..."
                                        value={inputValue}
                                        onFocus={openMenu}
                                        disabled={!cardGameId || loadingExpansions}
                                        {...getInputProps()}
                                    />
                                )}
                            </Autocomplete>
                        </Pane>

                        <Pane>
                            <Strong size={300} display="block" marginBottom={4}>Caută după nume / cod</Strong>
                            <SearchInput
                                width={260}
                                placeholder="ex: Charizard sau SV01-001"
                                value={searchInput}
                                onChange={this.handleSearchChange}
                            />
                        </Pane>

                        <Button onClick={this.handleResetFilters} marginBottom={2}>
                            Resetează filtrele
                        </Button>
                    </Pane>

                    {!stockAccountId && (
                        <Alert intent="none" marginTop={12} title="Alege un depozit ca să începi">
                            Tot stocul este pe depozite. După ce alegi depozitul, poți căuta singles
                            sau filtra după joc / extensie.
                        </Alert>
                    )}
                </Pane>

                {/* Two-column workspace */}
                <Pane display="flex" gap={16} alignItems="flex-start">
                    {/* Preview */}
                    <Pane width={360} flexShrink={0} position="sticky" top={16}>
                        {this.renderPreview()}
                    </Pane>

                    {/* List */}
                    <Pane flex={1} minWidth={0}>
                        <Pane
                            display="flex"
                            alignItems="center"
                            gap={12}
                            marginBottom={8}
                            flexWrap="wrap"
                        >
                            <Heading size={400}>2. Modifică cantitățile</Heading>
                            <Pane flex={1} />

                            <Pane display="flex" alignItems="center" gap={6}>
                                <Switch
                                    checked={sortChangedFirst}
                                    onChange={(e) => this.setState({ sortChangedFirst: e.target.checked })}
                                />
                                <Text size={300}>Modificările sus</Text>
                            </Pane>

                            {changesCount > 0 && (
                                <Badge color="yellow">
                                    {changesCount} modificare{changesCount === 1 ? '' : 'a'}
                                    {changesCount === 1 ? '' : ''}
                                </Badge>
                            )}

                            {loadingCards && <Spinner size={16} />}
                        </Pane>

                        {truncated && (
                            <Alert intent="warning" marginBottom={8}>
                                Sunt prea multe rezultate — se afișează doar primele.
                                Restrânge filtrele (alege extensia sau caută mai precis) pentru a
                                vedea toate cărțile.
                            </Alert>
                        )}

                        {/* Header */}
                        {cards.length > 0 && (
                            <Pane
                                display="grid"
                                gridTemplateColumns="48px 1fr 110px 110px 70px 70px 110px"
                                columnGap={8}
                                padding={8}
                                background="tint2"
                                borderRadius={4}
                                fontSize={11}
                                fontWeight={600}
                            >
                                <Text size={300}> </Text>
                                <Text size={300}>NUME</Text>
                                <Text size={300}>COD</Text>
                                <Text size={300}>VARIANTĂ / RARITATE</Text>
                                <Text size={300} textAlign="right">CURENT</Text>
                                <Text size={300} textAlign="center">Δ</Text>
                                <Text size={300} textAlign="center">CANTITATE NOUĂ</Text>
                            </Pane>
                        )}

                        {/* Rows */}
                        <Pane maxHeight="60vh" overflowY="auto" border="muted" borderTop="none" borderRadius={4}>
                            {cards.length === 0 && !loadingCards && (
                                <Pane padding={24} textAlign="center" color="muted">
                                    {stockAccountId
                                        ? 'Caută o carte după nume/cod sau alege un joc / extensie.'
                                        : 'Alege depozitul mai întâi.'}
                                </Pane>
                            )}
                            {orderedCards.map(this.renderRow)}
                        </Pane>

                        {/* Save bar */}
                        <Pane
                            display="flex"
                            alignItems="center"
                            gap={12}
                            marginTop={16}
                            padding={12}
                            background="tint1"
                            borderRadius={8}
                            flexWrap="wrap"
                        >
                            <Pane display="flex" alignItems="center" gap={6}>
                                <Switch
                                    checked={onlyChangesInCsv}
                                    onChange={(e) => this.setState({ onlyChangesInCsv: e.target.checked })}
                                />
                                <Text size={300}>Include în CSV doar rândurile modificate</Text>
                            </Pane>

                            <Pane flex={1} />

                            <Button
                                intent="danger"
                                onClick={this.handleResetEdits}
                                disabled={changesCount === 0}
                            >
                                Anulează modificările
                            </Button>

                            <Button
                                appearance="primary"
                                intent="success"
                                onClick={this.handleSaveCsv}
                                disabled={
                                    cards.length === 0 ||
                                    (onlyChangesInCsv && changesCount === 0)
                                }
                            >
                                Descarcă CSV ({onlyChangesInCsv ? changesCount : cards.length} rânduri)
                            </Button>
                        </Pane>
                    </Pane>
                </Pane>
            </Pane>
        );
    }
}

export default SingleWizard;
